import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AsyncSelect, ControlledTextField } from 'src/components/forms';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import { EntityWithIdFields } from 'src/types';
import { HttpLEP } from '../api/httpLEP';
import { useLEPEmisorTipos } from '../hooks/useLEPCache';
import { OperacionLEPEmisorUpdate, OperacionLEPEmisorView, schemaOperacionLEPEmisorUpdate } from '../types/dto';
import { OperacionLEPEmisorFields } from '../types/fields';
import { ContextOperacionType, ContextOperacion } from 'src/pages/sistema/RefactorLayout';
import { PermissionType } from 'src/types/security';

interface EditableCellProps {
    isEditing: boolean;
    onEdit: () => void;
    onCancel: () => void;
    children: React.ReactNode;
    displayValue: React.ReactNode;
    disabled?: boolean;
}

const EditableCell = ({ isEditing, onEdit, onCancel, children, displayValue, disabled }: EditableCellProps) => {
    if (!isEditing) {
        return (
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {displayValue}
                </Typography>
                {!disabled && (
                    <Tooltip title="Editar">
                        <IconButton onClick={onEdit} size="small">
                            <EditIcon fontSize="small" color="info" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={1} width="100%">
            <Box flex={1}>
                {children}
            </Box>
            <Tooltip title="Cancelar edición">
                <IconButton onClick={onCancel} size="small" color="error">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

interface OperacionLepEmisorFilaProps {
    operacionId: number;
    codSistema: number;
    emisor: OperacionLEPEmisorView;
    refetch: () => void;
    onDelete: (id: number) => void;
}

export const OperacionLepEmisorFila = ({ operacionId, codSistema, emisor, refetch, onDelete }: OperacionLepEmisorFilaProps) => {
    const [editMode, setEditMode] = useState<{ field: 'razonSocial' | 'tipo' | null }>({ field: null });
    const [isSaving, setIsSaving] = useState(false);

    const { emisorTipos, isLoading: loadingTipos, isError: errorTipos, error: errTipos } = useLEPEmisorTipos(codSistema);
    const loadTiposOptions = useLoadOptions({ data: emisorTipos, loading: loadingTipos, isError: errorTipos, error: errTipos });

    const formMethods = useForm<OperacionLEPEmisorUpdate>({
        resolver: zodResolver(schemaOperacionLEPEmisorUpdate),
        defaultValues: {
            [EntityWithIdFields.Id]: emisor[EntityWithIdFields.Id],
            [OperacionLEPEmisorFields.RazonSocial]: emisor[OperacionLEPEmisorFields.RazonSocial],
            [OperacionLEPEmisorFields.CodOperacionLEPEmisorTipo]: emisor[OperacionLEPEmisorFields.CodOperacionLEPEmisorTipo],
        }
    });

    const { control, handleSubmit, reset, watch, formState: { isDirty } } = formMethods;
    const watchedValues = watch();
    const { setSavePromise, permisoTipo } = useContext<ContextOperacionType>(ContextOperacion)
    

    const handleUpdate: SubmitHandler<OperacionLEPEmisorUpdate> = (data) => {
        console.log('data ----', data)
        setIsSaving(true);

        const promise = HttpLEP.updateEmisor(data, operacionId, emisor.id);

        toast.promise(promise, {
            loading: 'Guardando...',
            success: 'Emisor actualizado',
            error: (e) => `Error: ${e}`
        })
            .then(() => {
                refetch();
                setEditMode({ field: null });
                reset(data);
            })
            .finally(() => setIsSaving(false));
    };

    useEffect(() => {
        console.log(watchedValues)
        console.log(isDirty)
        if (!isDirty) {
            return;
        }

        if (Object.values(watchedValues).some(v => v === null)) {

            return;
        }

        const useDebounce = editMode.field === 'razonSocial';

        const onErrors = (errors: any) => console.error("Errores de validación:", errors);

        console.log('useDebounce', useDebounce)
        if (useDebounce) {
            const timer = setTimeout(() => handleSubmit(handleUpdate)(), 1000);

            return () => clearTimeout(timer);
        } else {
            handleSubmit(handleUpdate, onErrors)();
        }

    }, [JSON.stringify(watchedValues)]);

    return (
        <TableRow>
            <TableCell>
                <EditableCell
                    isEditing={editMode.field === 'razonSocial'}
                    onEdit={() => setEditMode({ field: 'razonSocial' })}
                    onCancel={() => {
                        reset();
                        setEditMode({ field: null });
                    }}
                    displayValue={emisor[OperacionLEPEmisorFields.RazonSocial]}
                    disabled={(permisoTipo < PermissionType.Write) || isSaving}
                >
                    <ControlledTextField
                        name={OperacionLEPEmisorFields.RazonSocial}
                        control={control}
                        size="small"
                        fullWidth
                        autoFocus
                    />
                    {permisoTipo}
                </EditableCell>
            </TableCell>

            <TableCell>
                <EditableCell
                    isEditing={editMode.field === 'tipo'}
                    onEdit={() => setEditMode({ field: 'tipo' })}
                    onCancel={() => {
                        reset();
                        setEditMode({ field: null });
                    }}
                    displayValue={emisor[OperacionLEPEmisorFields.DescOperacionLEPEmisorTipo]}
                    disabled={(permisoTipo < PermissionType.Write) || isSaving}
                >
                    <AsyncSelect
                        name={OperacionLEPEmisorFields.CodOperacionLEPEmisorTipo}
                        control={control}
                        loadOptions={loadTiposOptions}
                        size="small"
                        fullWidth
                        selectNotNulleable
                    />
                </EditableCell>
            </TableCell>

            {/* <TableCell align="right">
                {emisor[OperacionLEPEmisorFields.PermiteEditar] && (
                    <Tooltip title="Eliminar">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(emisor.id)}
                            disabled={isSaving}
                        >
                            <Icon icon="tabler:trash" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell> */}
        </TableRow>
    );
};
